import type { FormEvent, ReactNode } from 'react';
import { usePortal } from '../Portal';
import { Icon } from './Icon';
import { Spinner } from './Spinner';

interface StepLayoutProps {
  title: string;
  paragraph?: string;
  /** Centred visual above the title, e.g. the success icon; centres the whole step. */
  lead?: ReactNode;
  children?: ReactNode;
  error?: string | null;
  busy?: boolean;
  onBack?: () => void;
  backLabel?: string;
  onNext?: () => void;
  nextLabel?: string;
  nextDisabled?: boolean;
  /** Short status shown beside the buttons, e.g. how many items are in the claim. */
  summary?: ReactNode;
}

export function StepLayout({
  title,
  paragraph,
  lead,
  children,
  error,
  busy = false,
  onBack,
  backLabel,
  onNext,
  nextLabel,
  nextDisabled = false,
  summary,
}: StepLayoutProps) {
  const { t } = usePortal();

  const hasActions = Boolean(onBack || onNext);

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (onNext && !busy && !nextDisabled) onNext();
  };

  return (
    <form className={`nvd-claim-portal__step${lead ? ' nvd-claim-portal__step--centered' : ''}`} onSubmit={handleSubmit} noValidate>
      {lead}
      <h2 className="nvd-claim-portal__step-title">{title}</h2>
      {paragraph && <p className="nvd-claim-portal__step-text">{paragraph}</p>}

      {children && <div className="nvd-claim-portal__step-body">{children}</div>}

      {/* Without a button row the error has nowhere else to go, and nothing below it to push. */}
      {error && !hasActions && (
        <p className="nvd-claim-portal__error" role="alert">
          {error}
        </p>
      )}

      {hasActions && (
        <div className="nvd-claim-portal__actions">
          {onBack && (
            <button type="button" className="nvd-claim-portal__button nvd-claim-portal__button--back" onClick={onBack} disabled={busy}>
              {backLabel ?? t('common.back')}
            </button>
          )}
          {/* The error sits beside the button that caused it, in the row's own height, so it never
              pushes the fields up or the buttons down. It takes the summary's place while shown. */}
          {error ? (
            <p className="nvd-claim-portal__actions-error" role="alert">
              <Icon name="alert" className="nvd-claim-portal__actions-error-icon" />
              <span>{error}</span>
            </p>
          ) : (
            summary && <p className="nvd-claim-portal__actions-summary">{summary}</p>
          )}
          {onNext && (
            <button type="submit" className="nvd-claim-portal__button nvd-claim-portal__button--next" disabled={busy || nextDisabled} aria-busy={busy}>
              {busy ? <Spinner small /> : (nextLabel ?? t('common.next'))}
            </button>
          )}
        </div>
      )}
    </form>
  );
}
